/**
 * <SalesControlBar salesScope={useSalesScope()} brands={[...]} categories={[...]} onRefresh={refresh} loading={loading} />
 *
 * The single control bar for a sales page. Everything on the page should read the same scope.
 *   - Period preset pills (Yesterday, 7 days, 30 days, This month, Last month, Custom). Custom shows
 *     From / To inputs with validation (from <= to, at most 366 days, not after today) and Apply.
 *   - Compare segmented control: "Previous period" | "Same period last year".
 *   - Platform / Brand / Category selects. Options: arrays of strings or { value, label }.
 *     platforms defaults to every known platform code; labels use platformLabel().
 *     Pass hide={{ platform: true }} (or brand / category / search) to drop a control.
 *   - Optional SKU/product search (showSearch), debounced into scope.search.
 *   - Active-filter chips, each removable, plus "Clear all"; the human period label; Refresh button.
 * Sticky at the top from md up (override with stickyClassName, e.g. "md:top-14"), wraps on mobile.
 * `children` render at the end of the first row (e.g. an Export button).
 */
import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SALES_PLATFORM_CODES, platformLabel } from '../../config/platforms';
import { dayCount, indiaToday, isIsoDay } from './format';
import { useDebouncedValue } from './useAbortableGet';

const MAX_DAYS = 366;
const COMPARE_OPTIONS = [
  { value: 'previous', label: 'Previous period' },
  { value: 'last_year', label: 'Same period last year' },
];

const toOptions = (list, labeller = value => value) => (list || [])
  .map(item => (typeof item === 'string' ? { value: item, label: labeller(item) } : { value: item.value, label: item.label ?? labeller(item.value) }))
  .filter(option => option.value && option.value !== 'ALL');

/** Returns an error sentence for a draft range, or '' when it is valid. */
export const validateRange = (from, to, today = indiaToday()) => {
  if (!isIsoDay(from) || !isIsoDay(to)) return 'Pick both a start and an end date.';
  if (from > to) return 'Start date must be on or before the end date.';
  if (to > today) return 'End date cannot be in the future.';
  if (dayCount(from, to) > MAX_DAYS) return `Pick a range of ${MAX_DAYS} days or fewer.`;
  return '';
};

const FilterSelect = ({ id, value, onChange, allLabel, options, testId }) => (
  <Select value={value || 'ALL'} onValueChange={onChange}>
    <SelectTrigger id={id} aria-label={allLabel.replace(/^All /, 'Filter by ')} className={`h-9 w-full text-[13px] sm:w-44 ${value && value !== 'ALL' ? 'border-blue-400 bg-blue-50/60' : ''}`} data-testid={testId}>
      <SelectValue placeholder={allLabel} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="ALL">{allLabel}</SelectItem>
      {options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const SearchBox = ({ value, onChange }) => {
  const [text, setText] = useState(value || '');
  const debounced = useDebouncedValue(text, 350);
  useEffect(() => { setText(value || ''); }, [value]);
  useEffect(() => {
    if (debounced !== (value || '')) onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  return (
    <Input type="search" value={text} onChange={event => setText(event.target.value)} placeholder="Search SKU or product"
      aria-label="Search SKU or product" className="h-9 w-full text-[13px] sm:w-52" data-testid="sales-control-search" />
  );
};

const CustomRange = ({ scope, setScope }) => {
  const [from, setFrom] = useState(scope.date_from);
  const [to, setTo] = useState(scope.date_to);
  useEffect(() => { setFrom(scope.date_from); setTo(scope.date_to); }, [scope.date_from, scope.date_to]);
  const today = indiaToday();
  const error = validateRange(from, to, today);
  const dirty = from !== scope.date_from || to !== scope.date_to;
  const apply = event => {
    event.preventDefault();
    if (!error && dirty) setScope({ date_from: from, date_to: to });
  };
  return (
    <form onSubmit={apply} className="flex flex-wrap items-end gap-2" aria-label="Custom date range" data-testid="sales-custom-range">
      <label className="flex flex-col text-xs text-gray-600">From
        <Input type="date" value={from} max={to || today} onChange={event => setFrom(event.target.value)} className="h-9 w-[9.5rem] text-[13px]" aria-invalid={Boolean(error)} data-testid="sales-control-from" />
      </label>
      <label className="flex flex-col text-xs text-gray-600">To
        <Input type="date" value={to} min={from} max={today} onChange={event => setTo(event.target.value)} className="h-9 w-[9.5rem] text-[13px]" aria-invalid={Boolean(error)} data-testid="sales-control-to" />
      </label>
      <Button type="submit" size="sm" className="h-9" disabled={Boolean(error) || !dirty} data-testid="sales-control-apply">Apply</Button>
      {error && <p role="alert" className="w-full text-xs text-red-700 sm:w-auto sm:self-center">{error}</p>}
    </form>
  );
};

export const SalesControlBar = ({
  salesScope, platforms = SALES_PLATFORM_CODES, brands = [], categories = [], hide = {}, showSearch = false,
  onRefresh, loading = false, stickyClassName = 'md:sticky md:top-0', className = '', children,
}) => {
  const { scope, setScope, presets, preset, periodLabel, activeFilters } = salesScope;
  const [customOpen, setCustomOpen] = useState(preset === 'custom');
  // Follow the URL both ways (back/forward or an outside setScope can leave 'custom').
  useEffect(() => { setCustomOpen(preset === 'custom'); }, [preset]);
  const showCustom = customOpen || preset === 'custom';
  const platformOptions = toOptions(platforms, platformLabel);
  const brandOptions = toOptions(brands);
  const categoryOptions = toOptions(categories);
  const removeLabels = { platform: 'platform', brand: 'brand', category: 'category', sku: 'SKU', search: 'search' };

  const pickPreset = key => {
    if (key === 'custom') { setCustomOpen(true); setScope({ preset: 'custom' }); return; }
    setCustomOpen(false);
    setScope({ preset: key });
  };

  return (
    <section aria-label="Sales period and filters" data-testid="sales-control-bar"
      className={`${stickyClassName} z-20 -mx-4 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:mx-0 sm:rounded-xl sm:border ${className}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div role="group" aria-label="Period" className="flex flex-wrap gap-1">
          {presets.map(item => {
            const active = item.key === 'custom' ? showCustom : !showCustom && preset === item.key;
            return (
              <button key={item.key} type="button" aria-pressed={active} onClick={() => pickPreset(item.key)} data-testid={`sales-preset-${item.key}`}
                className={`h-8 rounded-full px-3 text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {item.label}
              </button>
            );
          })}
        </div>
        <div role="group" aria-label="Compare with" className="inline-flex rounded-lg bg-gray-100 p-0.5">
          {COMPARE_OPTIONS.map(option => {
            const active = scope.compare === option.value;
            return (
              <button key={option.value} type="button" aria-pressed={active} onClick={() => setScope({ compare: option.value })} data-testid={`sales-compare-${option.value}`}
                className={`h-7 rounded-md px-2.5 text-[12px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {option.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[13px] tabular-nums text-gray-600" aria-live="polite" data-testid="sales-period-label">{periodLabel}</span>
          {children}
          {onRefresh && (
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={onRefresh} disabled={loading} aria-label="Refresh sales data" data-testid="sales-control-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {showCustom && <div className="mt-3"><CustomRange scope={scope} setScope={setScope} /></div>}

      {(!hide.platform || !hide.brand || !hide.category || showSearch) && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {!hide.platform && <FilterSelect id="sales-control-platform" value={scope.platform} onChange={value => setScope({ platform: value })} allLabel="All platforms" options={platformOptions} testId="sales-control-platform" />}
          {!hide.brand && <FilterSelect id="sales-control-brand" value={scope.brand} onChange={value => setScope({ brand: value })} allLabel="All brands" options={brandOptions} testId="sales-control-brand" />}
          {!hide.category && <FilterSelect id="sales-control-category" value={scope.category} onChange={value => setScope({ category: value })} allLabel="All categories" options={categoryOptions} testId="sales-control-category" />}
          {showSearch && !hide.search && <SearchBox value={scope.search} onChange={value => setScope({ search: value, sku: '' })} />}
        </div>
      )}

      {activeFilters.length > 0 && (
        <ul className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Active filters" data-testid="sales-active-filters">
          {activeFilters.map(filter => (
            <li key={filter.key} className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2.5 pr-1 text-xs text-blue-900 ring-1 ring-inset ring-blue-200">
              {filter.label}
              <button type="button" onClick={() => setScope({ [filter.key]: '' })} aria-label={`Remove ${removeLabels[filter.key] || filter.key} filter: ${filter.label}`}
                className="rounded-full p-0.5 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid={`sales-chip-remove-${filter.key}`}>
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
          {activeFilters.length > 1 && (
            <li>
              <button type="button" onClick={salesScope.clearFilters} className="text-xs font-medium text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline" data-testid="sales-chip-clear-all">
                Clear all
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

export default SalesControlBar;
